/**
 * @fileoverview What to do when something happens that finishes advice
 * (TipEvent), as a pure decision TipsContext follows (B30 rev 5).
 *
 * Telling the server (the advice "done") is only needed when the brief lists
 * the event and not yet told in this visit. But a plant being added changes
 * what Today shows whether or not the held brief lists the event (the held
 * brief can be older than the filing), so the first-step read and the brief
 * are read again every time, with no reload; a stale one is what kept the
 * welcome's doors on screen after the first plant was filed.
 *
 * Where: TipsContext (happened).
 *
 * @module tips/happenedRules
 */

import type { TipEvent } from './types';

/** What happened() does for an event. */
export interface HappenedPlan {
  /** Tell the server the advice waiting for this event is done. */
  tell: boolean;
  /** Read the Today brief and the advice again afterwards. */
  refresh: boolean;
  /** Within that, invalidate the Today brief too: false when the write already did (gardenWritten), so the fetch under way is not cancelled and started again. */
  refreshBrief: boolean;
}

/**
 * Decides what an event needs.
 *
 * @param event - What happened.
 * @param listed - Whether the brief's readEvents lists it.
 * @param alreadyTold - Whether it was told already in this visit.
 * @param gardenWritten - Whether the write that caused the event already ran gardenWritten (it invalidated the brief).
 * @returns Whether to tell the server, whether to read the advice again, and whether to invalidate the brief too.
 */
export function happenedPlan(event: TipEvent, listed: boolean, alreadyTold: boolean, gardenWritten = false): HappenedPlan {
  const tell = listed && !alreadyTold;
  // A plant added always refreshes Today, told or not; other events only when the server was told.
  const refresh = tell || event === 'plant_added';
  return { tell, refresh, refreshBrief: refresh && !gardenWritten };
}
